import { memo } from 'react';
import { Box, Typography, Button, CircularProgress } from '@mui/material';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import FormModal from '../FormModal/FormModal';

const VARIANT_CONFIG = {
  danger: {
    icon: WarningAmberRoundedIcon,
    badgeBg: '#FEE2E2',
    badgeColor: '#DC2626',
    confirmBg: '#DC2626',
    confirmHoverBg: '#B91C1C',
  },
  success: {
    icon: CheckCircleRoundedIcon,
    badgeBg: '#DCFCE7',
    badgeColor: '#15803D',
    confirmBg: '#0F2350',
    confirmHoverBg: '#EA361A',
  },
  info: {
    icon: InfoRoundedIcon,
    badgeBg: '#DBEAFE',
    badgeColor: '#1D4ED8',
    confirmBg: '#0F2350',
    confirmHoverBg: '#EA361A',
  },
  error: {
    icon: ErrorRoundedIcon,
    badgeBg: '#FEE2E2',
    badgeColor: '#DC2626',
    confirmBg: '#0F2350',
    confirmHoverBg: '#EA361A',
  },
};

function ConfirmActionModal({
  open,
  onClose,
  onConfirm,
  variant = 'info',
  title,
  message,
  confirmLabel = 'Aceptar',
  cancelLabel = 'Cancelar',
  showCancel = false,
  loading = false,
}) {
  const { icon: Icon, badgeBg, badgeColor, confirmBg, confirmHoverBg } = VARIANT_CONFIG[variant] || VARIANT_CONFIG.info;

  return (
    <FormModal open={open} onClose={loading ? null : onClose} maxWidth="xs" fullWidth>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', pt: 1, pb: 1 }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '16px',
            bgcolor: badgeBg,
            color: badgeColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Icon sx={{ fontSize: 30 }} />
        </Box>

        {title && (
          <Typography sx={{ fontFamily: 'Merriweather, Georgia, serif', fontWeight: 800, fontSize: '18px', color: '#0F2350', mb: 1 }}>
            {title}
          </Typography>
        )}

        {message && (
          <Typography sx={{ fontFamily: 'Roboto, sans-serif', fontSize: '13.5px', color: '#57697F', lineHeight: 1.5, mb: 3 }}>
            {message}
          </Typography>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, width: '100%' }}>
          {showCancel && (
            <Button
              variant="outlined"
              onClick={onClose}
              disabled={loading}
              sx={{
                borderRadius: '10px',
                borderColor: '#CBD5E1',
                color: '#64748B',
                textTransform: 'none',
                fontWeight: 700,
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#94A3B8',
                  backgroundColor: '#F8FAFC',
                },
              }}
            >
              {cancelLabel}
            </Button>
          )}
          <Button
            variant="contained"
            onClick={onConfirm || onClose}
            disabled={loading}
            sx={{
              bgcolor: confirmBg,
              color: '#FFFFFF',
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: 700,
              px: 3,
              py: 1,
              boxShadow: 'none',
              minWidth: '110px',
              '&:hover': {
                bgcolor: confirmHoverBg,
                boxShadow: 'none',
              },
            }}
          >
            {loading ? <CircularProgress size={18} sx={{ color: '#FFFFFF' }} /> : confirmLabel}
          </Button>
        </Box>
      </Box>
    </FormModal>
  );
}

export default memo(ConfirmActionModal);
